import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  ChevronDown,
  ChevronRight,
  Eye,
  SearchIcon,
  Lightbulb,
  CheckCircle,
  Pencil,
  MessageSquare,
  Download,
  Settings,
  Users,
  User,
  Bot,
  Server,
  Lock,
  ExternalLink,
  Brain,
} from 'lucide-react'
import type { HistoryEntry, EntryCategory, ActorType } from '../../types/history'

const HASH_DISPLAY_LENGTH = 12

type HistoryTFunction = TFunction<'history'>

const categoryConfig: Record<EntryCategory, { icon: typeof Eye; color: string }> = {
  view: { icon: Eye, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  analysis: {
    icon: SearchIcon,
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  finding: {
    icon: Lightbulb,
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  decision: {
    icon: CheckCircle,
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  edit: {
    icon: Pencil,
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  annotation: {
    icon: MessageSquare,
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  },
  export: {
    icon: Download,
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  config: {
    icon: Settings,
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  },
  collaboration: {
    icon: Users,
    color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  },
}

const actorConfig: Record<ActorType, { icon: typeof User; color: string }> = {
  user: { icon: User, color: 'text-blue-600 dark:text-blue-400' },
  ai: { icon: Bot, color: 'text-indigo-600 dark:text-indigo-400' },
  system: { icon: Server, color: 'text-slate-500 dark:text-slate-400' },
}

type CategoryKey = `categories.${EntryCategory}`
type ActorKey = `actors.${ActorType}`

function getCategoryLabel(t: HistoryTFunction, category: EntryCategory): string {
  return (t as (key: CategoryKey) => string)(`categories.${category}`)
}

function getActorLabel(t: HistoryTFunction, actorType: ActorType): string {
  return (t as (key: ActorKey) => string)(`actors.${actorType}`)
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  })
}

interface EntryCardProps {
  entry: HistoryEntry
  expanded: boolean
  onToggle: () => void
  onViewArtifact?: ((id: string) => void) | undefined
  onConvertToMemory?: ((id: string) => void) | undefined
  onViewRelated?: ((ids: string[]) => void) | undefined
}

function CardHeader({
  entry,
  expanded,
  actConfig,
  catConfig,
}: {
  entry: HistoryEntry
  expanded: boolean
  actConfig: { icon: typeof User; color: string }
  catConfig: { icon: typeof Eye; color: string }
}): React.ReactElement {
  const { t } = useTranslation('history')
  const ActIcon = actConfig.icon
  const CatIcon = catConfig.icon
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-slate-400">
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-center gap-2">
          <div className={`flex items-center gap-1.5 ${actConfig.color}`}>
            <ActIcon className="h-4 w-4" />
            <span className="text-sm font-medium">
              {entry.actorType === 'ai'
                ? entry.llmModel
                : (entry.actorName ?? getActorLabel(t, entry.actorType))}
            </span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${catConfig.color}`}
          >
            <CatIcon className="h-3 w-3" />
            {getCategoryLabel(t, entry.category).toUpperCase()}
          </span>
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{entry.summary}</p>
        </div>
        <ContextBadges entry={entry} />
      </div>
      <div className="text-slate-300 dark:text-slate-600" title={t('immutableEntry')}>
        <Lock className="h-4 w-4" aria-label={t('immutableEntry')} />
      </div>
    </div>
  )
}

export function EntryCard({
  entry,
  expanded,
  onToggle,
  onViewArtifact,
  onConvertToMemory,
  onViewRelated,
}: EntryCardProps): React.ReactElement {
  const catConfig = categoryConfig[entry.category]
  const actConfig = actorConfig[entry.actorType]

  return (
    <div className="relative pl-24">
      <div className="absolute top-3 left-0 font-mono text-sm text-slate-500 dark:text-slate-400">
        {formatTime(entry.timestamp)}
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div
          className="cursor-pointer p-4 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
          onClick={onToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              onToggle()
            }
          }}
        >
          <CardHeader
            entry={entry}
            expanded={expanded}
            actConfig={actConfig}
            catConfig={catConfig}
          />
        </div>
        {expanded && (
          <ExpandedContent
            entry={entry}
            onViewArtifact={onViewArtifact}
            onConvertToMemory={onConvertToMemory}
            onViewRelated={onViewRelated}
          />
        )}
      </div>
    </div>
  )
}

function ContextBadges({ entry }: { entry: HistoryEntry }): React.ReactElement {
  const { t } = useTranslation('history')

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {entry.artifactType && (
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
          {entry.artifactType}
        </span>
      )}
      {entry.endpointName && (
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
          {entry.endpointName}
        </span>
      )}
      {entry.methodology?.confidence !== undefined && (
        <span className="rounded bg-sky-100 px-2 py-0.5 text-xs text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">
          {t('confidence', { value: entry.methodology.confidence })}
        </span>
      )}
      {entry.convertedToMemory && (
        <span className="flex items-center gap-1 rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
          <Brain className="h-3 w-3" />
          {t('memory')}
        </span>
      )}
    </div>
  )
}

interface ExpandedContentProps {
  entry: HistoryEntry
  onViewArtifact?: ((id: string) => void) | undefined
  onConvertToMemory?: ((id: string) => void) | undefined
  onViewRelated?: ((ids: string[]) => void) | undefined
}

function MethodologyBlock({
  methodology,
}: {
  methodology: HistoryEntry['methodology']
}): React.ReactElement | null {
  const { t } = useTranslation('history')
  if (!methodology) return null
  return (
    <div className="space-y-2 rounded-lg bg-indigo-50 p-3 dark:bg-indigo-900/20">
      <p className="text-xs font-medium tracking-wide text-indigo-700 uppercase dark:text-indigo-400">
        {t('aiMethodology')}
      </p>
      <p className="text-sm text-slate-700 dark:text-slate-300">{methodology.reasoning}</p>
      {methodology.mitreTechniques && methodology.mitreTechniques.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {methodology.mitreTechniques.map((tech) => (
            <span
              key={tech}
              className="rounded bg-red-100 px-2 py-0.5 font-mono text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400"
            >
              {tech}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function StateDiff({
  beforeState,
  afterState,
}: {
  beforeState: unknown
  afterState: unknown
}): React.ReactElement {
  const { t } = useTranslation('history')
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
        <p className="mb-1 text-xs font-medium text-red-700 dark:text-red-400">{t('before')}</p>
        <pre className="overflow-auto text-xs text-slate-600 dark:text-slate-400">
          {JSON.stringify(beforeState, null, 2)}
        </pre>
      </div>
      <div className="rounded-lg bg-emerald-50 p-3 dark:bg-emerald-900/20">
        <p className="mb-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
          {t('after')}
        </p>
        <pre className="overflow-auto text-xs text-slate-600 dark:text-slate-400">
          {JSON.stringify(afterState, null, 2)}
        </pre>
      </div>
    </div>
  )
}

function ExpandedContent({
  entry,
  onViewArtifact,
  onConvertToMemory,
  onViewRelated,
}: ExpandedContentProps): React.ReactElement {
  const { t } = useTranslation('history')
  return (
    <div className="border-t border-slate-100 px-4 pb-4 dark:border-slate-800">
      <div className="space-y-4 pt-4 pl-7">
        {entry.details && (
          <pre className="font-mono text-sm text-xs leading-relaxed whitespace-pre-wrap text-slate-600 dark:text-slate-400">
            {typeof entry.details === 'string'
              ? entry.details
              : JSON.stringify(entry.details, null, 2)}
          </pre>
        )}
        <MethodologyBlock methodology={entry.methodology} />
        {entry.beforeState && entry.afterState && (
          <StateDiff beforeState={entry.beforeState} afterState={entry.afterState} />
        )}
        <div className="flex items-center gap-4 border-t border-slate-100 pt-2 font-mono text-xs text-slate-400 dark:border-slate-800 dark:text-slate-500">
          <span title="Entry hash">#{entry.entryHash.slice(0, HASH_DISPLAY_LENGTH)}...</span>
          <span title={formatFullDate(entry.timestamp)}>
            {t('sequence', { number: entry.sequenceNumber })}
          </span>
        </div>
        <EntryActions
          entry={entry}
          onViewArtifact={onViewArtifact}
          onConvertToMemory={onConvertToMemory}
          onViewRelated={onViewRelated}
        />
      </div>
    </div>
  )
}

interface EntryActionsProps {
  entry: HistoryEntry
  onViewArtifact?: ((id: string) => void) | undefined
  onConvertToMemory?: ((id: string) => void) | undefined
  onViewRelated?: ((ids: string[]) => void) | undefined
}

function EntryActions({
  entry,
  onViewArtifact,
  onConvertToMemory,
  onViewRelated,
}: EntryActionsProps): React.ReactElement {
  const { t } = useTranslation('history')

  return (
    <div className="flex items-center gap-2 pt-2">
      {entry.artifactId && (
        <button
          onClick={() => {
            if (entry.artifactId) onViewArtifact?.(entry.artifactId)
          }}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t('viewArtifact')}
        </button>
      )}
      {entry.relatedEntryIds && entry.relatedEntryIds.length > 0 && (
        <button
          onClick={() => {
            if (entry.relatedEntryIds) onViewRelated?.(entry.relatedEntryIds)
          }}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          {t('related', { count: entry.relatedEntryIds.length })}
        </button>
      )}
      {!entry.convertedToMemory && (
        <button
          onClick={() => {
            onConvertToMemory?.(entry.id)
          }}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-indigo-600 transition-colors hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
        >
          <Brain className="h-3.5 w-3.5" />
          {t('convertToMemory')}
        </button>
      )}
    </div>
  )
}
